/**
 * THE PAGE SHE CAME FOR, KEPT THROUGH THE SIGN-IN (the owner's decision of 2026-10-19, ops #76:
 * clients reach only home, and every link ops puts in front of a client, the invoice email's link,
 * the PDF, "How did we do?", paying and the Zelle page, leads to home; `docs/seam.md` P107).
 *
 * A signed-out visitor to a deeper page, `/invoices/<id>?pay=bank` say, is drawn the door where she
 * is (`screens/Door.tsx`), and its Sign in carries that page to the sign-in steps as their one query
 * word, `next` (`signInFor`). Once her code has gone in, the sign-in hands it to Home in the history
 * (`afterSignIn`), and Home, which knows whether her household has come down yet, sends her on to
 * it, or draws the waiting page and sends her on once the household comes (`screens/Home.tsx`).
 *
 * - ONE RULE SAYS WHAT A RETURN TARGET MAY BE (`pageCameFor`), read wherever one is read: a path
 *   on home's own origin, among the places a signed-in person reaches (`PLACES_TO_COME_BACK_TO`),
 *   with its query, and nothing else. Never a full address, a protocol-relative path, a backslash,
 *   a space or a control character, a path the browser would read as another, the sign-in page,
 *   the page Stripe sends her back to, or a path no place is drawn at: each is no target, and she
 *   lands on Home as before. So no link sends her off home's own origin by way of the sign-in.
 * - A LINK PROVES NOTHING (CLAUDE.md binding rules 2 and 3). The page she is brought back to asks
 *   for her session and her membership as any page does, and answers not found for anything outside
 *   it, as an invoice's page goes to Payments and Payments to Home for a person shown no money.
 * - A RELOAD IN THE MIDDLE keeps it, since it is in the sign-in page's own address. ONE CODE EMAIL
 *   OPENED IN ANOTHER TAB, and a sign-in finished there: once she asks for a code from a sign-in
 *   that carries it, her browser keeps it for the code's thirty minutes in the `localStorage` her
 *   tabs share (`keepCameFor`), and a sign-in page with none of its own takes it from there
 *   (`cameForKept`). A sign-in forgets it.
 * - A BROWSER THAT KEEPS NOTHING keeps nothing, and anything this file did not write, or that no
 *   longer passes the rule, is none: nothing breaks, and the sign-in lands on Home.
 */
import { matchPath } from 'react-router-dom';
import {
  ACCOUNT_AGREEMENT_PATH,
  ACCOUNT_PATH,
  ACCOUNT_PAYING_PATH,
  ACCOUNT_PEOPLE_PATH,
  ACCOUNT_SENDING_PATH,
  FEEDBACK_ROUTE,
  INVOICE_ROUTE,
  MESSAGES_PATH,
  NEXT_QUERY,
  PAYMENTS_PATH,
  SCHEDULE_PATH,
  SIGN_IN_PATH,
  VISIT_ROUTE,
} from '../paths.ts';

/**
 * The places she may be brought back to: every place a signed-in person reaches through `Entry`
 * (`App.tsx`) but Home, where a sign-in lands anyway. Not the sign-in page, and not the page Stripe
 * sends her back to, which notes her return as it opens and is no page a link leads to. One visit's
 * report among them, where the visit-done email's "See the report and photos" leads (home #21).
 */
export const PLACES_TO_COME_BACK_TO: readonly string[] = [
  SCHEDULE_PATH,
  PAYMENTS_PATH,
  INVOICE_ROUTE,
  MESSAGES_PATH,
  FEEDBACK_ROUTE,
  VISIT_ROUTE,
  ACCOUNT_PATH,
  ACCOUNT_PAYING_PATH,
  ACCOUNT_PEOPLE_PATH,
  ACCOUNT_AGREEMENT_PATH,
  ACCOUNT_SENDING_PATH,
];

/** The longest return target taken: far longer than any link ops writes. */
const LONGEST = 1024;

/** An origin no browser reaches, against which a path is read to see where it would lead. */
const READ_AGAINST = 'https://home.invalid';

/**
 * A backslash, which a browser reads as a slash, and any space or control character, which a
 * browser drops or reads otherwise: none is in a path home writes or ops links to.
 */
const READ_OTHERWISE = /[\s\\\u0000-\u001f\u007f-\u009f]/;

/**
 * The page she came for, as a path and its query on home's own origin, or null for anything that
 * is not one of the places she may be brought back to. A fragment is left behind.
 */
export function pageCameFor(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > LONGEST) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || READ_OTHERWISE.test(raw)) return null;
  let read: URL;
  try {
    read = new URL(raw, READ_AGAINST);
  } catch {
    return null;
  }
  if (read.origin !== READ_AGAINST) return null;
  // The path as the browser would read it must be the path as written: no `.` or `..` part, and
  // nothing the browser would encode or move.
  if (read.pathname !== raw.split(/[?#]/, 1)[0]) return null;
  const placed = PLACES_TO_COME_BACK_TO.some(
    (route) => matchPath({ path: route, end: true }, read.pathname) !== null,
  );
  return placed ? `${read.pathname}${read.search}` : null;
}

/** The sign-in page, carrying the page she came for as its one query word, or alone with none. */
export function signInFor(target: string | null): string {
  return target === null
    ? SIGN_IN_PATH
    : `${SIGN_IN_PATH}?${new URLSearchParams({ [NEXT_QUERY]: target }).toString()}`;
}

/** The page she came for, read from where the door is drawn: none at Home or anywhere else. */
export function cameForHere(location: {
  readonly pathname: string;
  readonly search: string;
}): string | null {
  return pageCameFor(`${location.pathname}${location.search}`);
}

/** Where her browser keeps the page she came for once she asks for a code, across her tabs. */
export const CAME_FOR = 'home.signIn.cameFor';

/** How long it is kept: the thirty minutes the code she asked for works. */
export const CAME_FOR_KEPT_MS = 30 * 60 * 1000;

/** How far ahead of this tab's clock a kept instant may be and still be read: a minute. */
const CLOCK_SLACK_MS = 60 * 1000;

/** Keeps the page she came for, with when, as she asks for a code; nothing that fails the rule. */
export function keepCameFor(target: string, now: number = Date.now()): void {
  const kept = pageCameFor(target);
  if (kept === null) return;
  try {
    localStorage.setItem(CAME_FOR, JSON.stringify({ to: kept, at: now }));
  } catch {
    // A browser that keeps nothing: the sign-in page's own address still carries it.
  }
}

/** Forgets the page she came for, as a sign-in does, and as one kept too long is. */
export function forgetCameFor(): void {
  try {
    localStorage.removeItem(CAME_FOR);
  } catch {
    // Nothing kept.
  }
}

/** The page she came for, kept within the code's thirty minutes, or null. */
export function cameForKept(now: number = Date.now()): string | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(CAME_FOR);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let kept: unknown;
  try {
    kept = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof kept !== 'object' || kept === null || Array.isArray(kept)) return null;
  const { to, at } = kept as { readonly to?: unknown; readonly at?: unknown };
  if (typeof at !== 'number' || !Number.isFinite(at) || at > now + CLOCK_SLACK_MS) return null;
  if (now - at > CAME_FOR_KEPT_MS) {
    forgetCameFor();
    return null;
  }
  return pageCameFor(to);
}

/** What the sign-in hands Home in the history once her code has gone in. */
export interface AfterSignIn {
  readonly after: string;
}

/** The page she came for, as the sign-in handed it to Home, or null, by the same rule. */
export function afterSignIn(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  return pageCameFor((state as { readonly after?: unknown }).after);
}
